import { getApiErrorToastTitle } from "./apiErrorToast";

export const ROLE_CHANGE_BLOCKER_TYPE = {
  ACTIVE_VISITS: "active_visits",
  CONSULTANT_PROPERTIES: "consultant_properties",
  FINDER_PROPERTIES: "finder_properties",
  PHOTOGRAPHER_SESSIONS: "photographer_sessions",
  LAST_ADMIN: "last_admin",
} as const;

export type RoleChangeBlockerType =
  (typeof ROLE_CHANGE_BLOCKER_TYPE)[keyof typeof ROLE_CHANGE_BLOCKER_TYPE];

export type RoleChangeBlocker = {
  type: string;
  count?: number;
  message: string;
};

export const ROLE_ALREADY_ASSIGNED_MESSAGE = "O usuário já possui este papel";
export const INVALID_ROLE_MESSAGE = "Role inválida";
export const ROLE_CHANGE_GENERIC_ERROR = "Não foi possível alterar o papel";
export const CONSULTANT_REASSIGN_PATH = "/consultants/update";

type RoleChangeErrorShape = {
  response?: {
    status?: number;
    data?: {
      message?: unknown;
      errors?: Record<string, unknown>;
      data?: {
        blockers?: unknown;
      };
    };
  };
};

export type ParsedRoleChangeError = {
  blockers: RoleChangeBlocker[];
  messages: string[];
  fallbackMessage: string;
};

function getErrorStatus(error: unknown): number | undefined {
  return (error as RoleChangeErrorShape)?.response?.status;
}

function getErrorPayload(error: unknown): RoleChangeErrorShape["response"] {
  return (error as RoleChangeErrorShape)?.response;
}

function normalizeBlocker(raw: unknown): RoleChangeBlocker | null {
  if (!raw || typeof raw !== "object") return null;

  const blocker = raw as { type?: unknown; count?: unknown; message?: unknown };
  if (typeof blocker.message !== "string" || !blocker.message.trim()) {
    return null;
  }

  return {
    type: typeof blocker.type === "string" ? blocker.type : "",
    count: typeof blocker.count === "number" ? blocker.count : undefined,
    message: blocker.message.trim(),
  };
}

export function extractRoleChangeBlockers(error: unknown): RoleChangeBlocker[] {
  const blockers = getErrorPayload(error)?.data?.data?.blockers;
  if (!Array.isArray(blockers)) return [];

  return blockers
    .map(normalizeBlocker)
    .filter((blocker): blocker is RoleChangeBlocker => blocker != null);
}

export function hasConsultantPropertiesBlocker(blockers: RoleChangeBlocker[]): boolean {
  return blockers.some(
    (blocker) => blocker.type === ROLE_CHANGE_BLOCKER_TYPE.CONSULTANT_PROPERTIES
  );
}

function firstValidationError(errors: Record<string, unknown> | undefined): string {
  if (!errors) return "";

  for (const value of Object.values(errors)) {
    if (Array.isArray(value) && typeof value[0] === "string" && value[0].trim()) {
      return value[0].trim();
    }
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }

  return "";
}

export function getRoleChangeValidationMessage(error: unknown): string {
  const payload = getErrorPayload(error)?.data;
  const candidates = [
    typeof payload?.message === "string" ? payload.message.trim() : "",
    firstValidationError(payload?.errors),
  ].filter(Boolean);

  for (const candidate of candidates) {
    if (/já possui|already has|already assigned/i.test(candidate)) {
      return ROLE_ALREADY_ASSIGNED_MESSAGE;
    }
    if (/inválid|invalid/i.test(candidate)) {
      return INVALID_ROLE_MESSAGE;
    }
  }

  return candidates[0] || INVALID_ROLE_MESSAGE;
}

export function parseRoleChangeError(error: unknown): ParsedRoleChangeError {
  const status = getErrorStatus(error);
  const blockers = extractRoleChangeBlockers(error);

  if (status === 400 && blockers.length > 0) {
    return {
      blockers,
      messages: blockers.map((blocker) => blocker.message),
      fallbackMessage: getApiErrorToastTitle(error, ROLE_CHANGE_GENERIC_ERROR),
    };
  }

  if (status === 422) {
    const message = getRoleChangeValidationMessage(error);
    return {
      blockers: [],
      messages: [message],
      fallbackMessage: message,
    };
  }

  const fallbackMessage = getApiErrorToastTitle(error, ROLE_CHANGE_GENERIC_ERROR);
  return {
    blockers: [],
    messages: [fallbackMessage],
    fallbackMessage,
  };
}
